import styled from '@emotion/styled';
import { CountrySelect } from '@travelnest/merlin.country-select';
import * as React from 'react';
import { IValidationResult, withValidation, } from '../../../components/validation/ValidationWrap';
import { FormInput, FormLabel, FormLabelHelp, FormRow } from '../../form';
import { IAddress } from '../index';

interface IManualAddressInputProps {
	address: IAddress;
	validation: IValidationResult[];
	excludedCountries?: string[];
	onChange(next: IAddress): any;
}

const StyledCountrySelect = styled.div<{ valid: boolean; }>`
	Select {
		&:hover, :focus, :active {
			border: 1px solid var(--colour-input-border);
			color: #444;
			background: #fff;
		}
		border: 1px solid var(--colour-input-border);
		border-radius: 0;
		max-width: 400px;
		height: 2.8125rem;
		line-height: var(--line-height-c1);
		font-size: var(--font-size-c1);
		color: #444;
		background: #fff;
		${({ valid }) => !valid && `
			border-color: var(--colour-error-border);
			outline: 1px solid var(--colour-error-border);
			color: var(--colour-error-text);
			background-color: var(--colour-error-background);
			&:hover, :focus, :active {
				border-color: var(--colour-error-border);
				outline: 1px solid var(--colour-error-border);
				color: var(--colour-error-text);
				background-color: var(--colour-error-background);
			}
		`};
	}

	div::before,
	div::after {
		content: none;
	}
`;

const FormInputWithValidation = withValidation(FormInput);

export const ManualAddressInput: React.FC<IManualAddressInputProps> = ({ address, onChange, validation, excludedCountries }) => {
	const [countryUntouched, setCountryUntouched] = React.useState<boolean>(true);
	return (
		<div className="manual-address-input">
			<FormRow>
				<FormLabel htmlFor="address1">Address line 1</FormLabel>
				<FormInputWithValidation
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						onChange({ ...address, ['address1']: e.target.value })
					}
					type="text"
					required
					name="correspondence_address1"
					id="address1"
					value={address.address1}
					validation={validation}
					hideValidationUntilBlurred
				/>
			</FormRow>

			<FormRow>
				<FormLabel htmlFor="address2">Address line 2</FormLabel>
				<FormInputWithValidation
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						onChange({ ...address, ['address2']: e.target.value })
					}
					type="text"
					name="correspondence_address2"
					id="address2"
					value={address.address2}
					validation={validation}
					hideValidationUntilBlurred
				/>
			</FormRow>

			<FormRow>
				<FormLabel htmlFor="city">City</FormLabel>
				<FormLabelHelp>Or town/village/hamlet etc</FormLabelHelp>
				<FormInputWithValidation
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						onChange({ ...address, ['city']: e.target.value })
					}
					type="text"
					required
					name="correspondence_city"
					id="city"
					value={address.city}
					validation={validation}
					hideValidationUntilBlurred
				/>
			</FormRow>

			<FormRow>
				<FormLabel htmlFor="postcode">Postcode</FormLabel>
				<FormInputWithValidation
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						onChange({ ...address, ['postcode']: e.target.value })
					}
					type="text"
					required
					name="correspondence_postcode"
					id="postcode"
					value={address.postcode}
					validation={validation}
					hideValidationUntilBlurred
				/>
			</FormRow>

			<FormRow>
				<FormLabel htmlFor="country">Country</FormLabel>
				<StyledCountrySelect valid={countryUntouched || (validation
					.filter(
						(v) => v.key === 'correspondence_country_code')
					.map((v) => v.result.valid)[0])}>
					<CountrySelect
						onChange={(isoCode: string | undefined) => {
							if (isoCode !== undefined) {
								onChange({ ...address, ['country_code']: isoCode, });
							}
							setCountryUntouched(false);
						}}
						valid={
							validation
								.filter(
									(v) => v.key === 'correspondence_country_code')
								.map((v) => v.result.valid)[0]
						}
						value={address.country_code}
						placeholder="Select a country..."
						excludedCountries={excludedCountries}
					/>
				</StyledCountrySelect>
			</FormRow>
		</div>
	);
};
